import { ref, getCurrentInstance } from 'vue'
import store from '@/store'

/**
 * Carga la configuración de asientos de una zona para el gestor de bloqueos.
 *
 * Vive en un composable y no en el SFC porque hace una petición y gestiona su
 * ciclo de vida: la vista sólo debe renderizar `loading`, `error` y los datos.
 * Antes el dispatch estaba en el componente y sólo apagaba `loading` en el
 * callback de éxito, así que un fallo dejaba el loader activo para siempre y
 * propagaba un rechazo sin gestionar.
 *
 * @param {Object} options
 * @param {(data: Object) => void} [options.onLoaded] - se invoca con los datos
 *   crudos de la respuesta tras cada carga correcta, para que la vista actualice
 *   lo que dependa de ellos.
 * @returns {{ loading: import('vue').Ref<boolean>, error: import('vue').Ref<string|null>, loadZona: (id: string|number) => Promise<Object|null> }}
 */
export function useZonaSeats({ onLoaded } = {}) {
    const instance = getCurrentInstance()
    const config = instance?.proxy?.config ?? store.getters.getConfig

    const loading = ref(false)
    const error = ref(null)

    /**
     * Carga la zona y devuelve sus datos, o `null` si falló. El error queda
     * expuesto en `error` para que la vista pueda ofrecer un reintento en lugar
     * de quedarse cargando indefinidamente.
     *
     * @param {string|number} id
     * @returns {Promise<Object|null>}
     */
    async function loadZona(id) {
        loading.value = true
        error.value = null

        try {
            const params = {
                filters: {
                    zona_id: id,
                    conf: true
                }
            }

            const res = await store.dispatch(config.getVuexAlias('zonas.index'), params)
            const data = res?.data ?? {}

            if (typeof onLoaded === 'function') {
                onLoaded(data)
            }

            return data
        } catch (err) {
            console.error('Error al cargar la zona:', err)
            error.value = err?.response?.data?.message || 'No se pudo cargar la zona'
            return null
        } finally {
            loading.value = false
        }
    }

    return { loading, error, loadZona }
}
